import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import { FaPlus } from "react-icons/fa"
import { FiSettings } from "react-icons/fi"

import useT from "utils/hooks/useT"
import { ConfigContainer, Popup } from "components"
import { Container, Title, Text, Select } from "components/Form"
import { limsCommentLibrary, message } from "state_management"
import {
  COMMENT_CONTEXTS,
  commentContextLabelKey,
  selectCommentTemplates,
} from "state_management/lims/limsCommentLibrary"
import { selectCanAuthorMasterData } from "state_management/permissions"

// Comment-library admin screen (F1, SPEC-033 / PRD-008). A capability-admin (planner / qa-manager)
// curates the team's canned standard comments PER SIGNATURE CONTEXT. The signer dropdown in
// ESignatureModal offers the ENABLED templates for the relevant context; a disabled template is
// retired (hidden from signers) but kept so historical audit text stays readable. Editing is plain
// stamdata maintenance — audit-tracked, NOT e-signed (the signature that USES a comment is e-signed).
//
// Gated on selectCanAuthorMasterData — planner only since SPEC-058 B / BE SPEC-059 (the server 403s
// anyone else on POST/PATCH; a qa-manager holder could author until then). The whole route is also
// feature-flagged behind showLims in LimsScreen.
export default function CommentLibrary() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const templates = useSelector(selectCommentTemplates)
  const canAuthor = useSelector(selectCanAuthorMasterData)

  // null = closed; {} = creating; a template object = editing that record.
  const [editing, setEditing] = useState(null)

  useEffect(() => {
    dispatch(limsCommentLibrary.fetch())
  }, [dispatch])

  // Non-planners should never reach here (route is planner-gated), but guard the UI too so a deep
  // link shows a clear message rather than a broken editing surface.
  if (!canAuthor) {
    return (
      <div className="LimsCommentLibrary">
        <div className="LimsCommentLibrary-header">
          <h1 className="LimsCommentLibrary-title">{t("limsCommentLibrary")}</h1>
        </div>
        <p className="LimsCommentLibrary-empty">{t("limsCommentLibraryAdminOnly")}</p>
      </div>
    )
  }

  const handleSave = (record) => {
    const isCreating = !record.id
    const payload = {
      context: record.context,
      text: (record.text || "").trim(),
      sortOrder: Number.isFinite(Number(record.sortOrder)) ? Number(record.sortOrder) : 0,
    }
    const verb = isCreating
      ? dispatch(limsCommentLibrary.create(payload))
      : dispatch(limsCommentLibrary.update(record.id, payload))
    verb
      .then(() => {
        dispatch(message.success(t(isCreating ? "limsCommentCreated" : "limsCommentUpdated")))
        setEditing(null)
      })
      .catch(() => dispatch(message.error(t("limsCommentSaveFailed"))))
  }

  const handleToggleEnabled = (template) => {
    // A legacy/undefined `enabled` reads as enabled (=== false is the only "off"); toggle flips it.
    const currentlyEnabled = template.enabled !== false
    dispatch(limsCommentLibrary.setEnabled(template.id, !currentlyEnabled)).catch(() =>
      dispatch(message.error(t("limsCommentSaveFailed")))
    )
  }

  return (
    <div className="LimsCommentLibrary">
      <div className="LimsCommentLibrary-header">
        <h1 className="LimsCommentLibrary-title">{t("limsCommentLibrary")}</h1>
        <Button variant="primary" onClick={() => setEditing({})}>
          <FaPlus /> {t("limsCommentAdd")}
        </Button>
      </div>
      <p className="LimsCommentLibrary-intro">{t("limsCommentLibraryIntro")}</p>

      {COMMENT_CONTEXTS.map((context) => {
        const forContext = templates
          .filter((tpl) => tpl.context === context)
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || (a.text || "").localeCompare(b.text || ""))
        return (
          <div key={context} className="LimsCommentLibrary-group">
            <h2 className="LimsCommentLibrary-group-title">{t(commentContextLabelKey(context))}</h2>
            {forContext.length === 0 ? (
              <p className="LimsCommentLibrary-empty">{t("limsCommentNoneInContext")}</p>
            ) : (
              <Table striped hover responsive className="LimsCommentLibrary-table">
                <thead>
                  <tr>
                    {/* #4077: "Order" plus a bare number said nothing. The header now names
                        what the number controls, and the tooltip spells out the direction. */}
                    <th className="LimsCommentLibrary-col-order" title={t("limsCommentSortOrderHint")}>
                      {t("limsCommentSortOrder")}
                    </th>
                    <th>{t("limsCommentText")}</th>
                    <th className="LimsCommentLibrary-col-enabled">{t("limsCommentEnabled")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {forContext.map((tpl) => (
                    <tr key={tpl.id} className={tpl.enabled === false ? "LimsCommentLibrary-row--disabled" : undefined}>
                      <td className="LimsCommentLibrary-col-order">{tpl.sortOrder || 0}</td>
                      <td>{tpl.text}</td>
                      {/* RR8 #4067: same class as the <th> so the switch and its header share one
                          column geometry and alignment across all per-context tables. */}
                      <td className="LimsCommentLibrary-col-enabled">
                        <Form.Check
                          type="switch"
                          checked={tpl.enabled !== false}
                          onChange={() => handleToggleEnabled(tpl)}
                          aria-label={t("limsCommentEnabled")}
                        />
                      </td>
                      <td className="LimsCommentLibrary-actions">
                        <FiSettings
                          className="LimsCommentLibrary-edit-icon"
                          title={t("edit")}
                          onClick={() => setEditing(tpl)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )
      })}

      {editing && (
        <CommentTemplateConfig
          template={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

// Add / edit form for a single comment template, rendered in a Popup with a ConfigContainer
// (which owns the save/cancel buttons via submitHandler/cancelHandler, mirroring ConsumableConfig).
// A new template defaults to the first context; text is required; sort order drives the dropdown
// order. submitHandler returns false to keep the dialog open when the text is blank (fail-fast).
function CommentTemplateConfig({ template, onClose, onSave }) {
  const { t } = useT("common")
  const isCreating = !template
  const [record, setRecord] = useState(() =>
    template ? { ...template } : { context: COMMENT_CONTEXTS[0], text: "", sortOrder: 0 }
  )

  const handleSubmit = () => {
    if (!(record.text || "").trim() || !record.context) return false
    onSave(record)
    return true
  }

  return (
    <Popup nonScrollable onCancel={onClose}>
      <ConfigContainer
        title={isCreating ? t("limsCommentAdd") : t("limsCommentEdit")}
        cancelHandler={onClose}
        submitHandler={handleSubmit}
      >
        <Container>
          <Title>{t("limsCommentContext")}</Title>
          <Select
            isMulti={false}
            collection={COMMENT_CONTEXTS.map((c) => ({ id: c, name: t(commentContextLabelKey(c)) }))}
            value={record.context}
            onChange={(v) => setRecord((r) => ({ ...r, context: v[0] || COMMENT_CONTEXTS[0] }))}
            menuPlacement="bottom"
          />
        </Container>
        <Container>
          <Title>
            {t("limsCommentText")} <div className="red-text">*</div>
          </Title>
          <textarea
            className="LimsCommentLibrary-textarea"
            rows={3}
            value={record.text || ""}
            onChange={(e) => setRecord((r) => ({ ...r, text: e.target.value }))}
          />
        </Container>
        <Container>
          <Title>{t("limsCommentSortOrder")}</Title>
          <Text
            type="number"
            value={record.sortOrder === undefined || record.sortOrder === null ? "" : String(record.sortOrder)}
            onChange={(v) => setRecord((r) => ({ ...r, sortOrder: v }))}
          />
        </Container>
      </ConfigContainer>
    </Popup>
  )
}
